/**
 * Como um valor vira texto de tela, e como um texto digitado vira valor.
 *
 * Formatador e mascara moram juntos porque sao a mesma fronteira, nas duas
 * direcoes: de dado para o que a pessoa le, e do que ela digita de volta.
 */
/**
 * Um numero inteiro como se le em portugues: 2473 vira "2.473".
 *
 * O painel antigo escreve `{totalCollaborators || 0}` direto no JSX, sem separador.
 * Num cartao de painel, que existe para ser lido de relance, quatro digitos sem
 * separador custam uma parada - B-08 em `demandas/`.
 */
export function formatInteger(value) {
    return (value ?? 0).toLocaleString('pt-BR');
}
/** A data de hoje em `yyyy-MM-dd`, no fuso local - nao em UTC. */
export function todayIso() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}
/**
 * A pressao arterial, que o backend guarda como UM inteiro: `sistolica * 1000 + diastolica`.
 *
 * Decodificado por aritmetica, e nao por fatia de string. O painel antigo pega os
 * DOIS ultimos digitos como diastolica e compensa dividindo o resto por 10 quando
 * sobram quatro - o que funciona por acaso enquanto a diastolica tem dois
 * digitos, e quebra a partir de 100: `130100` sai como `130/00`.
 */
export function formatBloodPressure(value) {
    if (!value || value < 1000) {
        return '-';
    }
    const rounded = Math.round(value);
    const systolic = Math.floor(rounded / 1000);
    const diastolic = rounded % 1000;
    return `${systolic}/${String(diastolic).padStart(2, '0')} mmHg`;
}
/** A sistolica sozinha, que e o numero pelo qual a pressao e classificada. */
export function systolicOf(value) {
    return Math.floor(Math.round(value) / 1000);
}
/**
 * Uma DURACAO em segundos, como se le: `7h`, `7h 30min`, `30min`, `0h`.
 *
 * Zero e zero - um ponto no eixo de um grafico, e nao ausencia. Quem precisa
 * distinguir "nao dormiu" de "nao ha medicao" e o `formatSleep`, abaixo.
 */
export function formatDuration(seconds) {
    const total = Math.max(0, Math.round(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours > 0 && minutes > 0)
        return `${hours}h ${minutes}min`;
    if (hours > 0)
        return `${hours}h`;
    if (minutes > 0)
        return `${minutes}min`;
    return '0h';
}
/**
 * O sono como uma CELULA o escreve: igual a duracao, mas zero vira `-`.
 *
 * Numa tabela, zero segundo de sono quer dizer que nao houve medicao, e um traco diz
 * isso melhor que "0h". Num eixo de grafico e o contrario - zero e uma coordenada -,
 * e por isso o grafico usa o `formatDuration`. A mesma grandeza, duas leituras, e
 * cada uma com um nome.
 */
export function formatSleep(seconds) {
    return seconds > 0 ? formatDuration(seconds) : '-';
}
/**
 * Um numero em pt-BR com virgula decimal e separador de milhar: `36,51`, `1.234,5`.
 *
 * `places` e o numero EXATO de casas; `maxPlaces` deixa sumir os zeros do fim (`0,8`, e
 * nao `0,80`). Ate 08/10/2026 o painel escrevia com `toFixed`, que usa ponto: "+0.51°C".
 */
export function formatNumber(value, places, maxPlaces = places) {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: places, maximumFractionDigits: maxPlaces });
}
/**
 * A altura da anamnese, em metros: `1,78 m`.
 *
 * O servidor a manda em CENTIMETROS e inteira (`178`, e assim que o app grava), e o
 * painel escrevia o numero com um "m" colado - "178m". Valor abaixo de 3 so pode ser
 * metro (ninguem tem 3 cm), e e lido como tal: um cadastro antigo nao vira "0,02 m".
 */
export function formatHeight(value) {
    const meters = value >= 3 ? value / 100 : value;
    return `${formatNumber(meters, 2)} m`;
}
/** Como o valor de cada tipo se apresenta. Espelha `models/measurementType.ts`. */
export function formatMeasurement(typeId, value) {
    switch (typeId) {
        case 102:
            return formatBloodPressure(value);
        case 103:
            return `${formatNumber(value, 2)} °C`;
        case 153:
            return `${Math.round(value)} bpm`;
        case 154:
            return `${Math.round(value)} ms`;
        case 155:
            return `${Math.round(value)}% SpO₂`;
        case 156:
            return `${Math.round(value)}`;
        case 201:
            return `${value.toLocaleString('pt-BR')} passos`;
        case 202:
            return `${formatNumber(value, 1)} kcal`;
        case 203:
            return `${formatNumber(value / 1000, 0, 2)} km`;
        case 204:
            return formatSleep(value);
        case 205:
            return `${Math.round(value)} bpm`;
        default:
            return String(value);
    }
}
/** `2026-02-01T00:00:00` -> `01/02/2026`, por fatia, sem passar por `Date`. */
export function formatDate(iso) {
    if (!iso)
        return '-';
    const [year, month, day] = iso.slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
}
/** `2026-02-01T14:32:00` -> `14:32`. */
export function formatTime(iso) {
    return iso.includes('T') ? iso.slice(11, 16) : '-';
}
/**
 * A hora de um INSTANTE em milissegundos - `14:32:05`.
 *
 * Irma do `formatTime`, que recebe ISO; esta recebe epoch, porque e o que um eixo
 * numerico carrega. Mora aqui e nao na view: como se escreve uma hora nao depende de
 * quem desenha, e outra renderizacao escreveria a mesma coisa.
 */
export function formatClockTime(time) {
    const when = new Date(time);
    const dois = (value) => String(value).padStart(2, '0');
    return `${dois(when.getHours())}:${dois(when.getMinutes())}:${dois(when.getSeconds())}`;
}
/**
 * So o DIA de um instante - `01/02/2026`. Para a serie que e um ponto por dia (o sono
 * na empresa), onde a hora `00:00` do ponto nao e hora de nada.
 */
export function formatCalendarDay(time) {
    return new Date(time).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
/** O instante inteiro em pt-BR - `01/02/2026 14:32`. Mesma razao de morar aqui. */
export function formatInstant(time) {
    return new Date(time).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}
export function formatCnpj(digits) {
    const only = digits.replace(/\D/g, '').slice(0, 14);
    let out = only;
    if (only.length > 2)
        out = `${only.slice(0, 2)}.${only.slice(2)}`;
    if (only.length > 5)
        out = `${only.slice(0, 2)}.${only.slice(2, 5)}.${only.slice(5)}`;
    if (only.length > 8)
        out = `${only.slice(0, 2)}.${only.slice(2, 5)}.${only.slice(5, 8)}/${only.slice(8)}`;
    if (only.length > 12) {
        out = `${only.slice(0, 2)}.${only.slice(2, 5)}.${only.slice(5, 8)}/${only.slice(8, 12)}-${only.slice(12)}`;
    }
    return out;
}
/** Soma dias a uma data ISO, sem passar por `Date` e sem escorregar de fuso. */
export function addDays(iso, days) {
    const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
    // `Date.UTC` e aritmetica pura de calendario: nao ha fuso envolvido porque
    // entra e sai em UTC.
    const at = new Date(Date.UTC(year, month - 1, day + days));
    return at.toISOString().slice(0, 10);
}
export function addMonths(iso, months) {
    const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
    const at = new Date(Date.UTC(year, month - 1 + months, day));
    return at.toISOString().slice(0, 10);
}
/** Se o CPF fecha nos dois digitos verificadores. Portado de `utills/validations.ts`. */
export function isValidCpf(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {
        return false;
    }
    const digitAt = (upTo, weight) => {
        let sum = 0;
        for (let i = 0; i < upTo; i++) {
            sum += Number(digits.charAt(i)) * (weight - i);
        }
        const rest = 11 - (sum % 11);
        return rest >= 10 ? 0 : rest;
    };
    return digitAt(9, 10) === Number(digits.charAt(9)) && digitAt(10, 11) === Number(digits.charAt(10));
}
/** `000.000.000-00`, aplicada enquanto se digita. */
export function maskCpf(value) {
    const num = value.replace(/\D/g, '').slice(0, 11);
    if (num.length <= 3)
        return num;
    if (num.length <= 6)
        return num.replace(/(\d{3})(\d{1,3})/, '$1.$2');
    if (num.length <= 9)
        return num.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
    return num.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
}
/**
 * `(00) 00000-0000`, aplicada enquanto se digita.
 *
 * Transcricao do `setMaskPhone` da referencia, ramo por ramo - inclusive o
 * fechamento do parentese com DOIS digitos ("(54)") e os cinco digitos antes do
 * hifen, que num numero fixo de dez digitos sai como "(54) 99103-766".
 */
export function maskPhone(value) {
    const num = value.replace(/\D/g, '').slice(0, 11);
    if (num.length <= 2)
        return num.replace(/(\d{2})/, '($1)');
    if (num.length <= 7)
        return num.replace(/(\d{2})(\d{1,5})/, '($1) $2');
    return num.replace(/(\d{2})(\d{5})(\d{1,4})/, '($1) $2-$3');
}
/*
 * Aqui moravam `maskDate`, `isoFromTypedDate`, `typedDateFromIso` e `typedDateProblem`.
 *
 * As quatro existiam para um lugar so: os dois campos de data digitados a mao do filtro
 * dos Monitoramentos Compartilhados, a unica das cinco telas de periodo que nao usava o
 * seletor comum. Em 18/09/2026 ela passou a usa-lo, e com ele nao ha texto livre para
 * mascarar, converter nem validar - o componente so avisa quando as duas pontas formam um
 * dia completo e em ordem.
 *
 * Foram removidas em vez de deixadas: uma funcao de conversao de data sem chamador atrai o
 * proximo campo de data para o caminho que acabamos de abandonar.
 */
/** As 27 unidades da federacao, para o registro profissional. */
export const UF_LIST = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO'
];
/**
 * Os caracteres que contam como especial na senha.
 *
 * Copiada do painel antigo (`utills/schemas.ts`) caractere por caractere: uma
 * lista mais curta recusaria senha que o antigo aceita, e uma mais longa deixaria
 * passar senha que o backend pode nao querer.
 */
const SPECIAL_CHARACTERS = ['@', '#', '%', '=', '&', '!', '$', '*', ')', '(', '~', '+', '-', '/', '\\'];
/** O que falta na senha, ou nada. A mensagem e a mesma do painel antigo. */
export function passwordProblem(password) {
    const short = password.length < 6;
    const plain = !SPECIAL_CHARACTERS.some((char) => password.includes(char));
    if (short && plain) {
        return 'Deve conter no mínimo 6 caracteres e ao menos 1 caractere especial (@, #, %, =, &, !, $, *, ), (, ~, +, -, /, \\).';
    }
    if (short) {
        return 'Deve conter no mínimo 6 caracteres.';
    }
    if (plain) {
        return 'Deve conter ao menos 1 caractere especial (@, #, %, =, &, !, $, *, ), (, ~, +, -, /, \\).';
    }
    return undefined;
}
/** A variacao de temperatura sobre 36 °C, com sinal e duas casas: `+0,51 °C`. */
export function formatTemperatureVariation(delta) {
    return `${delta >= 0 ? '+' : ''}${formatNumber(delta, 2)} °C`;
}
/**
 * Um numero no EIXO de um grafico: virgula decimal, ate uma casa - `0,8`, `72`, `1.200`.
 *
 * Era `String(Math.round(value * 10) / 10)` na view, que escrevia `0.8`.
 */
export function formatAxisNumber(value) {
    return formatNumber(value, 0, 1);
}
/**
 * Um valor de metrica escrito como o CARTAO do painel o escreve.
 *
 * O balao do grafico usa esta mesma funcao. Antes ele formatava por conta propria, e o
 * mesmo valor saia de dois jeitos na mesma tela: `72.4` no balao, `72 bpm` no cartao
 * (auditoria de 08/10/2026). A temperatura chega como VARIACAO sobre 36 °C, que e o que
 * as curvas plotam. A pressao tem duas pontas e nao passa por aqui.
 *
 * Virgula decimal desde 08/10/2026; casas e unidades estao em discussao com o P.O.
 * (relatos 1 e 2 de `demandas/2026-10-07/usr-adm-geral`), e mudam aqui.
 */
export function formatMetricValue(metric, value) {
    switch (metric) {
        case 'FREQUENCIA_CARDIACA':
            return `${Math.round(value)} bpm`;
        case 'HRV':
            return `${Math.round(value)} ms`;
        case 'OXIGENACAO':
            return `${Math.round(value)}%`;
        case 'TEMPERATURA':
            return formatTemperatureVariation(value);
        // A mesma escrita do eixo e do cartao. Era `07:32` aqui e `7h 32min` no eixo.
        case 'SONO':
            return formatDuration(value);
        case 'PRESSAO':
            return String(Math.round(value));
    }
}
/*
 * Os numeros de LEITURA de um painel: com separador de milhar, virgula decimal, e "-"
 * quando nao ha valor.
 *
 * Diferem do `formatInteger` acima num ponto de proposito: la, ausente vira `0` (um
 * cartao de contagem sem dado e zero); aqui, ausente vira `-` (um indicador que nao
 * se calculou nao e zero). Eram tres copias - o presenter de Adocao e Engajamento, a
 * view dele e este arquivo - ate 08/10/2026, e as duas primeiras arredondavam os
 * minutos de uma duracao para CIMA ("1h 60min").
 */
const NUMBER = new Intl.NumberFormat('pt-BR');
const NUMBER_ONE_DECIMAL = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
function absent(value) {
    return value === null || value === undefined || Number.isNaN(value);
}
/** Uma contagem: `2.473`, ou `-` sem valor. */
export function formatCount(value) {
    return absent(value) ? '-' : NUMBER.format(Math.round(value));
}
/** Um numero com uma casa: `3,4`, ou `-` sem valor. */
export function formatDecimal(value) {
    return absent(value) ? '-' : NUMBER_ONE_DECIMAL.format(value);
}
/** Uma porcentagem com uma casa: `42,5%`, ou `-` sem valor. */
export function formatPercent(value) {
    return absent(value) ? '-' : `${NUMBER_ONE_DECIMAL.format(value)}%`;
}
/** Metros como quilometros: `12,3 km`, ou `-` sem valor. */
export function formatKm(meters) {
    return absent(meters) ? '-' : `${NUMBER_ONE_DECIMAL.format(meters / 1000)} km`;
}
/** `2026-09-14T16:32:00` -> `14/09/2026 às 16:32`; so a data quando nao ha hora. */
export function formatDateAtTime(iso) {
    if (!iso)
        return '-';
    const time = iso.slice(11, 16);
    return time ? `${formatDate(iso)} às ${time}` : formatDate(iso);
}
//# sourceMappingURL=format.js.map